// src/pages/PrivacyPolicyPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-dark" style={{ background: '#0A1628' }}>
      <SEO
        title="Privacy Policy — AeroTrick"
        description="AeroTrick privacy policy and legal terms — how we collect, use and protect your data. TDPSA compliant. Governed by the laws of Texas."
        canonical="/privacy-policy"
        noindex={true}
      />

      {/* Back Button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E293B] border border-[rgba(148,163,184,0.12)] text-[#CBD5E1] hover:text-white transition-all duration-200 hover:bg-[#263045]"
        >
          <span>←</span>
          <span>Back to Home</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-20 md:py-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">LEGAL TERMS & PRIVACY POLICY</h1>
          <div className="space-y-1 text-[#94A3B8]">
            <p className="text-sm">Effective Date: May 8, 2026</p>
            <p className="text-sm">Entity: Aerotrick LLC (formation in progress)</p>
            <p className="text-sm">Jurisdiction: Boerne, Kendall County, Texas, USA</p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">

          {/* Section 1 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">1. PRIVACY & DATA MINIMIZATION (EDGE COMPUTING)</h2>
            <p className="text-[#94A3B8] mb-4 italic">In compliance with the Texas Data Privacy and Security Act (TDPSA).</p>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Privacy by Design:</strong> Aerotrick operates on an "Edge-First" architecture. Our technology is designed to respect the privacy of individuals and airport tenants.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">On-Site Processing:</strong> AI agents process visual data locally on the airport's authorized hardware.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Zero Video Storage:</strong> No raw video footage or private images containing identifiable persons or properties are streamed, stored, or transmitted to Aerotrick LLC's cloud servers.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Metadata Only:</strong> We only collect and transmit non-sensitive metadata (such as identification strings, timestamps, and event categories) necessary for administrative and revenue management.</span>
              </li>
            </ul>
          </section>

          {/* Section 2 — NEW: Personal Data & TDPSA Rights */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">2. PERSONAL DATA COLLECTION & YOUR RIGHTS (TDPSA)</h2>
            <p className="text-[#94A3B8] mb-4 italic">Applicable to staff accounts, client accounts, and authorized platform users.</p>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Data We Collect:</strong> When accessing the Aerotrick platform, we collect account-related personal data including first and last name, email address, assigned role, and login activity logs. This data is strictly limited to what is necessary for platform access and operational accountability.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Purpose of Collection:</strong> Personal data is used solely for authentication, role-based access control, audit logging, and service communications. It is never sold, rented, or shared with third-party advertisers.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Data Retention:</strong> Personal account data is retained for the duration of the active service agreement and for up to 12 months following contract termination, after which it is permanently deleted or anonymized.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Right of Access:</strong> Under the TDPSA, you have the right to request confirmation of whether Aerotrick processes your personal data and to obtain a copy of that data.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Right of Correction:</strong> You may request that inaccurate personal data be corrected at any time.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Right of Deletion:</strong> You may request the deletion of your personal data, subject to applicable legal obligations and active contractual requirements.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">How to Exercise Your Rights:</strong> Submit your request by email to <a href="mailto:contact@aerotrick.com" className="text-[#3B82F6] hover:underline">contact@aerotrick.com</a> with the subject line "TDPSA Data Request." We will respond within 45 days as required by law.</span>
              </li>
            </ul>
          </section>

          {/* Section 3 — was 2 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">3. TRADEMARK & COPYRIGHT USAGE GUIDELINES</h2>
            <p className="text-[#94A3B8] mb-4 italic">Protection of Aerotrick's brand identity and assets.</p>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Ownership:</strong> The Aerotrick name, logo, website content, and the specific stylization of our brand are the exclusive property of Aerotrick LLC.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Prohibited Use:</strong> You may not incorporate "Aerotrick" or any recognizable portion of it into your company name, app name, domain name, or social media handle.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">AI Data Models & Scraping Ban:</strong> You may not use or scrape any trademarks, content, or data from Aerotrick for the creation or training of AI models, including but not limited to machine learning models and neural networks.</span>
              </li>
            </ul>
          </section>

          {/* Section 4 — was 3, expanded ToS */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">4. TERMS OF SERVICE</h2>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Nature of Service:</strong> Aerotrick provides automated administrative support for airport management. Our system utilizes a fusion of external aeronautical data feeds, telemetry, and local proprietary optical sensors.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Not a Safety System:</strong> Aerotrick is an administrative and billing support tool only. It is NOT an Air Traffic Control (ATC) system and is not certified for navigational safety or aircraft separation.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Billing Support Only:</strong> Aerotrick provides decision-support data. The final responsibility for the accuracy of any invoice issued lies exclusively with the Airport Administration.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Client Obligations:</strong> The subscribing organization (the "Client") is responsible for ensuring that all staff accounts are used in compliance with applicable federal and state regulations, including FAA operational rules. The Client agrees not to use the Aerotrick platform for any unlawful purpose or in a manner that interferes with the platform's integrity or availability.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Modifications to Terms:</strong> Aerotrick LLC reserves the right to update these Terms at any time. Clients will be notified of material changes by email at least 30 days prior to the new terms taking effect. Continued use of the platform after that date constitutes acceptance of the revised Terms.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Termination:</strong> Either party may terminate the service agreement with 30 days' written notice. Aerotrick LLC reserves the right to immediately suspend or terminate access in the event of a material breach of these Terms, non-payment, or any use of the platform that poses a legal or security risk. Upon termination, the Client's data will be retained for 12 months and then permanently deleted upon written request.</span>
              </li>
            </ul>
          </section>

          {/* Section 5 — was 4 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">5. INTELLECTUAL PROPERTY & PROPRIETARY TECHNOLOGY</h2>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Algorithm Protection:</strong> All software, including proprietary computer vision models, neural networks, and automated data fusion logic, is the exclusive property of Aerotrick LLC.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Restrictions:</strong> Users are strictly prohibited from reverse-engineering, decompiling, or attempting to extract the underlying AI architecture from Aerotrick's local agents.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Attribution:</strong> Any authorized reference to our brand must include the statement: "Aerotrick is a trademark of Aerotrick LLC."</span>
              </li>
            </ul>
          </section>

          {/* Section 6 — was 5 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">6. LIMITATION OF LIABILITY (TEXAS COMPLIANCE)</h2>
            <div className="space-y-3 text-[#CBD5E1]">
              <p><strong className="text-white">"AS-IS" CLAUSE:</strong> THE AEROTRICK PLATFORM IS PROVIDED "AS IS." TO THE MAXIMUM EXTENT PERMITTED BY TEXAS LAW, AEROTRICK LLC DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE.</p>
              <p><strong className="text-white">LIMITATION OF DAMAGES:</strong> AEROTRICK LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY THE CUSTOMER DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
            </div>
          </section>

          {/* Section 7 — was 6 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">7. COOKIE POLICY</h2>
            <ul className="space-y-3 text-[#CBD5E1]">
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">Essential Cookies:</strong> Required for secure login to the management dashboard.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3B82F6]">•</span>
                <span><strong className="text-white">No Marketing Tracking:</strong> We do not use cookies for third-party targeted advertising.</span>
              </li>
            </ul>
          </section>

          {/* Section 8 — was 7 */}
          <section className="bg-[#0F172A] border border-[rgba(148,163,184,0.12)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">8. GOVERNING LAW</h2>
            <p className="text-[#CBD5E1]">These Guidelines and Terms are governed by the laws of the State of Texas. Any legal action or dispute shall be settled exclusively in the courts of Kendall County, Texas.</p>
          </section>

          {/* Contact */}
          <section className="bg-[#0F172A] border border-[rgba(59,130,246,0.3)] rounded-xl p-6">
            <h2 className="text-xl font-semibold text-[#3B82F6] mb-3">CONTACT</h2>
            <p className="text-[#CBD5E1]">For any questions regarding these Terms, privacy requests, or data rights under the TDPSA, contact us at: <a href="mailto:contact@aerotrick.com" className="text-[#3B82F6] hover:underline">contact@aerotrick.com</a></p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-[rgba(148,163,184,0.12)] text-center">
          <p className="text-[#64748B] text-sm">© 2026 AeroTrick — Airport Management Solutions · aerotrick.com</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;